import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Palette, Copy, Download, Eye, RotateCcw } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface ShadowConfig {
  offsetX: number;
  offsetY: number;
  blurRadius: number;
  spreadRadius: number;
  color: string;
  inset: boolean;
}

const CSSBoxShadowGenerator: React.FC = () => {
  const [shadows, setShadows] = useState<ShadowConfig[]>([
    {
      offsetX: 0,
      offsetY: 4,
      blurRadius: 8,
      spreadRadius: 0,
      color: '#000000',
      inset: false
    }
  ]);
  const [opacity, setOpacity] = useState(25);
  const [cssCode, setCssCode] = useState('');

  useSEO({
    title: 'CSS Box Shadow Generator - Create Custom Box Shadows Online',
    description: 'Generate CSS box shadow code with visual preview. Create multiple shadows, adjust opacity, blur, spread, and position. Copy ready-to-use CSS code.',
    keywords: 'css box shadow generator, box shadow css, css shadow tool, shadow generator, css effects, web design tools',
    canonical: 'https://snap-tools.online/design/css-box-shadow-generator'
  });

  // Generate CSS code
  useEffect(() => {
    const shadowStrings = shadows.map(shadow => {
      const rgba = hexToRgba(shadow.color, opacity / 100);
      return `${shadow.inset ? 'inset ' : ''}${shadow.offsetX}px ${shadow.offsetY}px ${shadow.blurRadius}px ${shadow.spreadRadius}px ${rgba}`;
    });
    
    const css = `box-shadow: ${shadowStrings.join(', ')};`;
    setCssCode(css);
  }, [shadows, opacity]);

  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const updateShadow = (index: number, field: keyof ShadowConfig, value: any) => {
    const newShadows = [...shadows];
    newShadows[index] = { ...newShadows[index], [field]: value };
    setShadows(newShadows);
  };

  const addShadow = () => {
    setShadows([...shadows, {
      offsetX: 0,
      offsetY: 4,
      blurRadius: 8,
      spreadRadius: 0,
      color: '#000000',
      inset: false
    }]);
  };

  const removeShadow = (index: number) => {
    if (shadows.length > 1) {
      setShadows(shadows.filter((_, i) => i !== index));
    }
  };

  const resetShadows = () => {
    setShadows([{
      offsetX: 0,
      offsetY: 4,
      blurRadius: 8,
      spreadRadius: 0,
      color: '#000000',
      inset: false
    }]);
    setOpacity(25);
  };

  const copyCss = () => {
    navigator.clipboard.writeText(cssCode);
    toast.success('CSS code copied to clipboard!');
  };

  const downloadCss = () => {
    const blob = new Blob([cssCode], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'box-shadow.css';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('CSS file downloaded!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl">
                <Palette className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              CSS Box Shadow Generator
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Create beautiful box shadows with live preview. Adjust multiple shadows, opacity, and positioning. Generate ready-to-use CSS code.
            </p>
          </div>

          {/* Header Ad */}
          <div className="mb-12">
            <AdContainer
              adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
              className="max-w-4xl mx-auto"
              position="header"
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Controls */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Shadow Controls</h2>
              
              {/* Global Opacity */}
              <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Global Opacity: {opacity}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={opacity}
                  onChange={(e) => setOpacity(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              {/* Shadow List */}
              <div className="space-y-6">
                {shadows.map((shadow, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900">Shadow {index + 1}</h3>
                      <div className="flex gap-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={shadow.inset}
                            onChange={(e) => updateShadow(index, 'inset', e.target.checked)}
                            className="mr-2"
                          />
                          Inset
                        </label>
                        {shadows.length > 1 && (
                          <button
                            onClick={() => removeShadow(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Offset X: {shadow.offsetX}px
                        </label>
                        <input
                          type="range"
                          min="-50"
                          max="50"
                          value={shadow.offsetX}
                          onChange={(e) => updateShadow(index, 'offsetX', Number(e.target.value))}
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Offset Y: {shadow.offsetY}px
                        </label>
                        <input
                          type="range"
                          min="-50"
                          max="50"
                          value={shadow.offsetY}
                          onChange={(e) => updateShadow(index, 'offsetY', Number(e.target.value))}
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Blur: {shadow.blurRadius}px
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={shadow.blurRadius}
                          onChange={(e) => updateShadow(index, 'blurRadius', Number(e.target.value))}
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Spread: {shadow.spreadRadius}px
                        </label>
                        <input
                          type="range"
                          min="-50"
                          max="50"
                          value={shadow.spreadRadius}
                          onChange={(e) => updateShadow(index, 'spreadRadius', Number(e.target.value))}
                          className="w-full"
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Color
                        </label>
                        <input
                          type="color"
                          value={shadow.color}
                          onChange={(e) => updateShadow(index, 'color', e.target.value)}
                          className="w-full h-10 rounded border"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 mt-6">
                <button
                  onClick={addShadow}
                  className="btn-primary flex-1"
                >
                  Add Shadow
                </button>
                <button
                  onClick={resetShadows}
                  className="btn-secondary flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              </div>
            </div>

            {/* Preview & Output */}
            <div className="space-y-8">
              {/* Preview */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Eye className="w-6 h-6" />
                  Preview
                </h2>
                <div className="bg-gray-100 p-8 rounded-lg flex items-center justify-center min-h-[300px]">
                  <div
                    className="w-48 h-32 bg-white rounded-lg"
                    style={{ boxShadow: cssCode.replace('box-shadow: ', '').replace(';', '') }}
                  />
                </div>
              </div>

              {/* CSS Output */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">CSS Code</h2>
                <div className="relative">
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
                    <code>{cssCode}</code>
                  </pre>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={copyCss}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Copy CSS
                    </button>
                    <button
                      onClick={downloadCss}
                      className="btn-secondary flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Ad */}
          <div className="mt-12">
            <AdContainer
              adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
              className="max-w-4xl mx-auto"
              position="footer"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CSSBoxShadowGenerator;
